import React, { useState } from "react";
import { AlertTriangle, Plus, Loader, Edit, Trash2 } from "lucide-react";
import { useVehicleIssues } from "@/hooks/useVehicleIssues";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { getIssueColorByIdentifier } from "@/utils/colorUtils";
import { getLayoutConfig } from "@/config/vehicleLayouts";
import { formatDateTimeLocalInput } from "@/utils/dateFormat";
import { useTranslation } from "react-i18next";
import type { Vehicle, VehicleIssue } from "@/types";

interface VehicleIssuesSectionProps {
  vehicle: Vehicle;
  className?: string;
}

export function VehicleIssuesSection({
  vehicle,
  className = "",
}: VehicleIssuesSectionProps) {
  const { t } = useTranslation(["pages", "common"]);
  const { issues, loading, error, createIssue, updateIssue, deleteIssue } =
    useVehicleIssues({ vehicleId: vehicle?.id });

  // Get layout configuration - use the first issue's layout or fallback to vehicle model's default
  const getDisplayLayoutId = () => {
    // If there are issues, check if they all use the same layout
    if (issues.length > 0) {
      const uniqueLayoutIds = [
        ...new Set(issues.map((issue) => issue.layoutId)),
      ];
      if (uniqueLayoutIds.length === 1) {
        // All issues use the same layout
        return uniqueLayoutIds[0];
      }
      // Multiple layouts - we'll need to handle this case
      // For now, use the vehicle model's default and warn about mixed layouts
      if (uniqueLayoutIds.length > 1) {
        console.warn(
          "Mixed layout IDs found in vehicle issues:",
          uniqueLayoutIds,
        );
      }
    }
    // Fallback to vehicle model's layout ID or default
    return vehicle.vehicleModel?.layoutId || "regular";
  };

  const displayLayoutId = getDisplayLayoutId();
  const layoutConfig = getLayoutConfig(displayLayoutId);
  const layoutName = t(`pages:vehicles.layouts.${displayLayoutId}.name`);

  const [showForm, setShowForm] = useState(false);
  const [editingIssue, setEditingIssue] = useState<VehicleIssue | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    issue: VehicleIssue | null;
  }>({ isOpen: false, issue: null });
  const [selectedPosition, setSelectedPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  // Get local datetime string (YYYY-MM-DDTHH:mm) for input
  const getLocalDateTimeString = (date = new Date()) =>
    formatDateTimeLocalInput(date);

  // Helper function to convert local datetime string to UTC ISO string
  const localDateTimeToUTC = (localDateTime: string) => {
    return new Date(localDateTime).toISOString();
  };

  const [formData, setFormData] = useState({
    dateTime: getLocalDateTimeString(), // Local time for display
    description: "",
  });

  const handleLayoutClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.round(event.clientX - rect.left);
    const y = Math.round(event.clientY - rect.top);

    setSelectedPosition({ x, y });

    // If we're not already in form mode (editing or creating), start a new issue form
    if (!showForm) {
      setShowForm(true);
      // Reset form with current local date/time for new issues only
      setFormData({
        dateTime: getLocalDateTimeString(),
        description: "",
      });
    }
    // If we're already editing, just update the position (keep existing form data)
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedPosition || !vehicle?.id) return;

    const issueData = {
      vehicleId: vehicle.id,
      occurredAt: localDateTimeToUTC(formData.dateTime),
      coordX: selectedPosition.x,
      coordY: selectedPosition.y,
      description: formData.description || undefined,
      layoutId: displayLayoutId, // Use the current display layout ID
    };

    let success = false;

    if (editingIssue) {
      // Update existing issue
      const updatedIssue = await updateIssue(editingIssue.id, issueData);
      success = !!updatedIssue;
    } else {
      // Create new issue
      const newIssue = await createIssue(issueData);
      success = !!newIssue;
    }

    if (success) {
      setShowForm(false);
      setSelectedPosition(null);
      setEditingIssue(null);
      setFormData({
        dateTime: getLocalDateTimeString(),
        description: "",
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedPosition(null);
    setEditingIssue(null);
    setSelectedIssueId(null);
    setFormData({
      dateTime: getLocalDateTimeString(),
      description: "",
    });
  };

  const handleEditIssue = (issue: VehicleIssue) => {
    setEditingIssue(issue);
    setSelectedPosition({ x: issue.coordX, y: issue.coordY });
    setSelectedIssueId(issue.id);
    setFormData({
      dateTime: getLocalDateTimeString(new Date(issue.occurredAt)),
      description: issue.description || "",
    });
    setShowForm(true);
  };

  const handleIssueRowClick = (issue: VehicleIssue) => {
    // Toggle selection - if already selected, deselect
    if (selectedIssueId === issue.id) {
      setSelectedIssueId(null);
    } else {
      setSelectedIssueId(issue.id);
    }
  };

  const handleDeleteClick = (issue: VehicleIssue) => {
    setDeleteModal({ isOpen: true, issue });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.issue) {
      const success = await deleteIssue(deleteModal.issue.id);
      if (success) {
        setDeleteModal({ isOpen: false, issue: null });
      }
    }
  };

  return (
    <div
      className={`bg-white rounded-lg border border-secondary-200 ${className}`}
    >
      <div className="p-6 border-b border-secondary-100">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            {t("pages:vehicles.issues.title")}
          </h2>
          <div className="text-sm text-secondary-500">
            {t("pages:vehicles.issues.countMarked", { count: issues.length })}
          </div>
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-md font-medium text-secondary-700">
              {showForm && editingIssue
                ? t("pages:vehicles.issues.hintChange")
                : t("pages:vehicles.issues.hintMark")}
            </h3>
            <div className="flex gap-2">
              {layoutConfig.deprecated && (
                <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                  {t("pages:vehicles.issues.legacyBadge", { name: layoutName })}
                </div>
              )}
              {issues.length > 0 &&
                (() => {
                  const uniqueLayoutIds = [
                    ...new Set(issues.map((issue) => issue.layoutId)),
                  ];
                  if (uniqueLayoutIds.length > 1) {
                    return (
                      <div className="text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded">
                        {t("pages:vehicles.issues.mixedLayouts", {
                          count: uniqueLayoutIds.length,
                        })}
                      </div>
                    );
                  }
                  return null;
                })()}
            </div>
          </div>

          {/* Vehicle Layout Container */}
          <div
            className="relative w-full bg-secondary-50 border-2 border-dashed border-secondary-300 rounded-lg hover:border-orange-400 transition-colors"
            onClick={handleLayoutClick}
            style={{ height: "300px", minHeight: "200px" }} // 300px fixed height
          >
            {/* Vehicle Layout Image */}
            <div className="absolute inset-0 flex items-center justify-center p-8">
              <img
                src={`/${layoutConfig.image}`}
                alt={t("pages:vehicles.issues.layoutAlt", { name: layoutName })}
                className="max-w-full max-h-full object-contain pointer-events-none"
                draggable={false}
                style={{ width: "100%", height: "auto" }}
              />
            </div>

            {/* Existing Issue Markers */}
            {issues.map((issue) => {
              const isBeingEdited =
                editingIssue && editingIssue.id === issue.id;
              const issueColor = getIssueColorByIdentifier(issue.occurredAt);
              const isSelected = selectedIssueId === issue.id;

              return (
                <div
                  key={issue.id}
 className={`absolute rounded-full border-2 shadow-lg transform -translate-x-1/2 -translate-y-1/2 cursor-pointer hover:scale-110 transition-all ${
                    isBeingEdited
                      ? "w-6 h-6 border-green-500 ring-2 ring-green-300 ring-opacity-75 scale-125"
                      : isSelected
                        ? "w-6 h-6 border-primary-500 ring-2 ring-primary-300 ring-opacity-75 scale-125"
                        : `w-4 h-4 border-white ${issueColor.hoverClass}`
                  } ${issueColor.bgClass}`}
                  style={{
                    left: `${issue.coordX}px`,
                    top: `${issue.coordY}px`,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isBeingEdited) {
                      // If being edited, don't change behavior - the preview dot will handle repositioning
                      return;
                    } else if (isSelected) {
                      // If already selected, allow delete
                      handleDeleteClick(issue);
                    } else {
                      // If not selected, select it
                      handleIssueRowClick(issue);
                    }
                  }}
                  title={t("pages:vehicles.issues.markerTitle", {
                    description:
                      issue.description || t("pages:vehicles.issues.noDescription"),
                    date: new Date(issue.occurredAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                    state: isBeingEdited
                      ? t("pages:vehicles.issues.stateBeingEdited")
                      : isSelected
                        ? t("pages:vehicles.issues.stateClickToDelete")
                        : t("pages:vehicles.issues.stateClickToSelect"),
                  })}
                />
              );
            })}

            {/* Preview marker for current selection */}
            {selectedPosition &&
              showForm &&
              (() => {
                // Only show preview if we're creating a new issue OR repositioning an existing one
                const isRepositioning =
                  editingIssue &&
                  (selectedPosition.x !== editingIssue.coordX ||
                    selectedPosition.y !== editingIssue.coordY);
                const isCreatingNew = !editingIssue;

                if (!isRepositioning && !isCreatingNew) {
                  return null;
                }

                let previewColor;
                if (editingIssue) {
                  // When editing, use the issue's actual occurredAt timestamp
                  previewColor = getIssueColorByIdentifier(
                    editingIssue.occurredAt,
                  );
                } else {
                  // When creating new issue, use the form datetime converted to UTC
                  const previewOccurredAt = localDateTimeToUTC(
                    formData.dateTime,
                  );
                  previewColor = getIssueColorByIdentifier(previewOccurredAt);
                }

                return (
                  <div
 className={`absolute w-4 h-4 ${previewColor.bgClass} rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 animate-pulse`}
                    style={{
                      left: `${selectedPosition.x}px`,
                      top: `${selectedPosition.y}px`,
                    }}
                  />
                );
              })()}
          </div>

          <p className="text-sm text-secondary-500 mt-3 text-center">
            {showForm && editingIssue
              ? t("pages:vehicles.issues.editHint")
              : t("pages:vehicles.issues.createHint")}
          </p>
        </div>

        {/* Issue Form */}
        {showForm && (
          <div className="border-t border-secondary-200 pt-6">
            <h3 className="text-md font-medium text-secondary-700 mb-4">
              {editingIssue ? t("pages:vehicles.issues.editTitle") : t("pages:vehicles.issues.createTitle")}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="dateTime"
                  className="block text-sm font-medium text-secondary-700 mb-2"
                >
                  {t("pages:vehicles.issues.fields.dateTime")}
                </label>
                <input
                  type="datetime-local"
                  id="dateTime"
                  value={formData.dateTime}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      dateTime: e.target.value,
                    }))
                  }
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-secondary-700 mb-2"
                >
                  {t("pages:vehicles.issues.fields.descriptionOptional")}
                </label>
                <textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="input w-full resize-none"
                  placeholder={t("pages:vehicles.issues.descriptionPlaceholder")}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary px-4 py-2"
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary px-4 py-2 flex items-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  <span>
                    {loading
                      ? editingIssue
                        ? t("common:updating")
                        : t("pages:vehicles.issues.adding")
                      : editingIssue
                        ? t("pages:vehicles.issues.updateIssue")
                        : t("pages:vehicles.issues.addIssue")}
                  </span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Issues List */}
        {issues.length > 0 && (
          <div className="border-t border-secondary-200 pt-6 mt-6">
            <h3 className="text-md font-medium text-secondary-700 mb-4">
              {t("pages:vehicles.issues.recordedTitle", { count: issues.length })}
            </h3>
 <div className="space-y-3">
              {issues.map((issue) => {
                const issueColor = getIssueColorByIdentifier(issue.occurredAt);
                const isSelected = selectedIssueId === issue.id;
                const isBeingEdited =
                  editingIssue && editingIssue.id === issue.id;

                return (
                  <div
                    key={issue.id}
 className={`flex items-start justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                      isBeingEdited
                        ? "bg-green-100 border-2 border-green-400 shadow-md"
                        : isSelected
                          ? "bg-primary-100 border-2 border-primary-300 shadow-md"
                          : "bg-secondary-50 hover:bg-secondary-100"
                    }`}
                    onClick={() => handleIssueRowClick(issue)}
                  >
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-1">
                        <div
 className={`w-3 h-3 ${issueColor.bgClass} rounded-full`}
                        ></div>
 <span className="text-sm font-medium text-secondary-900">
                          {new Date(issue.occurredAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              timeZoneName: "short",
                            },
                          )}
                        </span>
                      </div>
                      {issue.description && (
 <p className="text-sm text-secondary-600 ml-5">
                          {issue.description}
                        </p>
                      )}
                      <p className="text-xs text-secondary-400 ml-5">
                        {t("pages:vehicles.issues.position", {
                          x: issue.coordX,
                          y: issue.coordY,
                        })}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditIssue(issue);
                        }}
                        className="p-2 text-secondary-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title={t("pages:vehicles.issues.editActionTitle")}
                        aria-label={t("pages:vehicles.issues.editIssueAria")}
                        disabled={loading}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(issue);
                        }}
                        className="p-2 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t("pages:vehicles.issues.deleteActionTitle")}
                        aria-label={t("pages:vehicles.issues.deleteIssueAria")}
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, issue: null })}
        onConfirm={handleConfirmDelete}
        title={t("pages:vehicles.issues.deleteModalTitle")}
        message={t("pages:vehicles.issues.deleteModalMessage", {
          description: deleteModal.issue?.description
            ? ` "${deleteModal.issue.description}"`
            : "",
        })}
        confirmButtonText={t("common:delete")}
        confirmButtonColor="red"
        isLoading={loading}
      />
    </div>
  );
}
